import { useEffect, useId, useState, type MouseEvent } from "react";
import { toneColor, type Tone } from "./stat-modal";

/* Barras horizontais de distribuicao (status, estrelas, planos...) */
export function BarList({ items, total }: { items: { label: string; value: number; tone?: Tone }[]; total?: number }) {
  const [ready, setReady] = useState(false);
  useEffect(() => { const r = requestAnimationFrame(() => setReady(true)); return () => cancelAnimationFrame(r); }, []);
  const sum = total ?? items.reduce((s, i) => s + i.value, 0);
  return (
    <div className="space-y-2.5">
      {items.map((it) => {
        const pct = sum ? (it.value / sum) * 100 : 0;
        return (
          <div key={it.label}>
            <div className="mb-1 flex justify-between text-[13px]">
              <span className="text-[#1f2621]">{it.label}</span>
              <span className="font-semibold">
                {it.value} <span className="font-normal text-[#8a9088]">· {pct.toFixed(0)}%</span>
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[#f0ede4]">
              <div
                className="h-full rounded-full transition-[width] duration-700 ease-out motion-reduce:transition-none"
                style={{ width: ready ? `${pct}%` : "0%", background: toneColor[it.tone ?? "ok"] }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* Grafico de area com 1+ series e tooltip no hover */
export function AreaChart({
  labels, series, height = 160,
}: {
  labels: string[];
  series: { name: string; color: string; values: number[] }[];
  height?: number;
}) {
  const uid = useId().replace(/:/g, "");
  const [hover, setHover] = useState<number | null>(null);
  const n = labels.length;
  if (n === 0) return <div className="py-10 text-center text-sm text-[#8a9088]">Sem dados no período</div>;

  const W = 400, H = 150, pad = 8;
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const x = (i: number) => (n <= 1 ? W / 2 : (i / (n - 1)) * W);
  const y = (v: number) => H - pad - (v / max) * (H - pad * 2);
  const line = (vals: number[]) => vals.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

  const onMove = (e: MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - r.left) / r.width) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };

  return (
    <div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="agro-draw block w-full"
          style={{ height }}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            {series.map((s, si) => (
              <linearGradient key={si} id={`${uid}-g${si}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={s.color} stopOpacity=".22" />
                <stop offset="1" stopColor={s.color} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>
          {series.map((s, si) => (
            <g key={s.name}>
              <path className="agro-fade" d={`${line(s.values)} L${x(n - 1)},${H} L${x(0)},${H} Z`} fill={`url(#${uid}-g${si})`} />
              <path d={line(s.values)} fill="none" stroke={s.color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
            </g>
          ))}
          {hover !== null && (
            <line x1={x(hover)} x2={x(hover)} y1={0} y2={H} stroke="#1a4a2e" strokeOpacity=".2" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          )}
        </svg>
        {series.every((s) => s.values.every((v) => !v)) && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-[#8a9088]">Sem movimento no período</div>
        )}
        {hover !== null && (
          <div
            className="pointer-events-none absolute top-1 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg border border-[#ebe7dc] bg-white px-2.5 py-1.5 text-[11.5px] shadow-md"
            style={{ left: `${Math.min(88, Math.max(12, (x(hover) / W) * 100))}%` }}
          >
            <div className="font-semibold text-[#1a4a2e]">{labels[hover]}</div>
            {series.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                {s.name}: <b>{s.values[hover] ?? 0}</b>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-[#8a9088]">
        <span>{labels[0]}</span>
        <div className="flex gap-3">
          {series.map((s) => (
            <span key={s.name} className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} /> {s.name}
            </span>
          ))}
        </div>
        <span>{labels[n - 1]}</span>
      </div>
    </div>
  );
}
